import { isPostalCodeRequired } from "@redotech/locale/countries";
import { useInput } from "@redotech/react-util/form";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { useAddressAutocompleteActions } from "@redotech/redo-customer-portal-shared/hooks/useAddressAutocomplete/use-address-autocomplete-actions";
import { useTranslation } from "@redotech/redo-i18n";
import {
  getCountryNameFromCode,
  RedoShopifyCountryCode,
} from "@redotech/redo-inventory-common/fulfillment-country-code";
import {
  AddressAutocompleteForm,
  AddressLockedValues,
  createAddressAutocompleteForm,
} from "@redotech/redo-web/address-autocomplete-form";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { InputSchema } from "@redotech/ui/form";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { ConfirmationButtonGroup } from "./confirmation-button-group";
import * as css from "./edit-address-modal.module.css";
import { StickyFooter } from "./sticky-footer";

export interface EditAddressModalData {
  initialValues: {
    name: string;
    address1: string;
    address2: string | null;
    city: string;
    province: string | null;
    zip: string;
    countryCode: RedoShopifyCountryCode;
    email: string | null;
    phone: string | null;
    company: string | null;
  };
  texts: { shipItemsTo: string; title: string };
  showEditAddressLoading: false;
}

export interface EditAddressModalProps {
  data: EditAddressModalData | { showEditAddressLoading: true };
  onConfirm: (
    formValues: InputSchema.Value<
      ReturnType<typeof createAddressAutocompleteForm>
    >,
  ) => Promise<void>;
  onClose: () => void;
  saving?: boolean;
  error?: string;
  requirePhone?: boolean;
  uiSelector?: string;
  lockedValues?: AddressLockedValues;
}

export const EditAddressModal = memo(function EditAddressModal({
  data,
  onConfirm,
  onClose,
  saving,
  error,
  requirePhone = false,
  uiSelector,
  lockedValues,
}: EditAddressModalProps) {
  const { t } = useTranslation();
  const screenSize = useScreenType();
  const actions = useAddressAutocompleteActions();

  const defaultFormValues = {
    name: "",
    address1: "",
    address2: null,
    city: "",
    province: null,
    zip: "",
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    countryCode: "US" as RedoShopifyCountryCode,
    email: null,
    phone: null,
    company: null,
  };

  const formSchema = useMemo(
    () => createAddressAutocompleteForm(true, requirePhone),
    [requirePhone],
  );
  const form = useInput(formSchema, defaultFormValues);

  const lockedCountryCode = lockedValues?.countryCode;
  const [addressClearedByLock, setAddressClearedByLock] = useState(false);
  const seeded = useRef(false);

  /** One effect, so seeding and the lock never overwrite each other in the same render. */
  useEffect(() => {
    if (data.showEditAddressLoading || !data.initialValues) {
      return;
    }
    const current = seeded.current ? form.value : data.initialValues;
    seeded.current = true;
    if (!lockedCountryCode || current.countryCode === lockedCountryCode) {
      if (current !== form.value) {
        form.setValue(current);
      }
      return;
    }
    form.setValue({
      ...current,
      countryCode: lockedCountryCode,
      address1: "",
      address2: null,
      city: "",
      province: null,
      zip: "",
    });
    setAddressClearedByLock(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.showEditAddressLoading, lockedCountryCode, form.value.countryCode]);

  const isFormValid = useMemo(() => {
    const zipValid =
      !isPostalCodeRequired(form.value.countryCode) ||
      Boolean(form.value.zip.trim());
    return (
      form.errors.length === 0 &&
      form.value.name.trim() &&
      form.value.address1.trim() &&
      form.value.city.trim() &&
      zipValid &&
      form.value.email?.trim()
    );
  }, [form.errors, form.value]);

  const handleConfirm = async () => {
    if (isFormValid) {
      await onConfirm(form.value);
    }
  };

  if (data.showEditAddressLoading) {
    return <LoadingRedoAnimation />;
  }

  const {
    texts: { shipItemsTo, title },
  } = data;

  return (
    <Flex
      className={
        screenSize === ScreenType.DESKTOP ? css.desktopCardLayout : undefined
      }
      data-ui={uiSelector ?? "edit-address"}
      dir="column"
      gap="2xl"
      pt="3xl"
      px="3xl"
    >
      <Text fontSize="lg" fontWeight="semibold" textColor="primary">
        {title}
      </Text>
      <Divider />
      <Flex dir="column" gap="3xl" pt="sm">
        <Text fontSize="md" textColor="secondary">
          {shipItemsTo}
        </Text>
        {addressClearedByLock && lockedCountryCode && (
          <Text
            as="p"
            data-ui="exchange-country-locked-notice"
            fontSize="sm"
            textColor="error"
          >
            {t(
              "Exchanges can only ship to {{country}}. Enter an address there.",
              { country: getCountryNameFromCode(lockedCountryCode) },
            )}
          </Text>
        )}

        <AddressAutocompleteForm
          actions={actions}
          autoCompleteOnName={false}
          form={form}
          hideCompanyField
          hideEmailField={false}
          includeNameField
          lockedValues={lockedValues}
          requireEmail
          requirePhone={requirePhone}
          showRequiredLabels
          useLegacyProvinceSelect
        />
      </Flex>
      {error && (
        <Text fontSize="sm" textColor="error">
          {error}
        </Text>
      )}
      <StickyFooter>
        <Flex dir="column" gap="3xl" pt="sm">
          <Divider />
          <ConfirmationButtonGroup
            primaryButtonProps={{
              dataTestId: "save-button",
              disabled: !isFormValid || saving,
              onClick: handleConfirm,
              pending: saving,
              text: t("Save"),
            }}
            secondaryButtonProps={{
              disabled: saving,
              onClick: onClose,
              text: t("Cancel"),
            }}
          />
        </Flex>
      </StickyFooter>
    </Flex>
  );
});
